/* EventsRenderer.js */

"use strict";
/* global Slick */
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const InputTextControl = uiCore.Control.Textfield.InputText;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const ButtonControl = uiCore.Control.Action.Button;
const NumberControl = uiCore.Control.Textfield.InputNumber;
const formatter = require("./cellFormaters");
const checkBoxEditor = require("./cellEditors/CheckBoxEditor");
const WorkflowEditor = require("./cellEditors/WorkflowEditor");
const FilterUsernamesComponent = require("./veil/filterUsernames/FilterUsernamesComponent");

class EventsRenderer {
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("events-gl");

        this._enableSyslogPort = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Activate Syslog for this Organization",
            title: "Enable syslog monitoring for the organization",
        });
        this._port = new InputTextControl({
            darkTheme: true,
            labelShown: true,
            label: "Syslog Port",
            title: "Gluware–assigned port that must used by devices to send syslog messages via UDP to Gluware",
            disabled: true,
        });

        this._enableACS = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Event Triggers for this Organization",
            title: "Take a configuration snapshot or run a workflow after the selected events",
        });
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            title: "Cancel",
            darkTheme: true,
            disabled: true,
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            title: "Save",
            darkTheme: true,
            disabled: true,
            active: true,
        });
        this._waitTimeInput = new NumberControl({
            darkTheme: true,
            labelShown: true,
            label: "Ignore duplicate messages within (min)",
            title: "Minutes to ignore syslog messages before snapshot taken",
            disabled: false,
        });

        this._filterUsernamesButton = new ButtonControl({
            label: "Filter Usernames",
            title: "Configure inbound syslog username filters",
            darkTheme: true,
        });

        this._contentContainer = document.createElement("div");
        this._contentContainer.classList.add("content");

        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._controlButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._confirmButton.renderer.getRootElement());
        const heading = document.createElement("h1");
        heading.innerText = "Events";
        this._rootElem.appendChild(heading);
        this._rootElem.appendChild(this._contentContainer);
        //this._rootElem.appendChild(this._createGluSection());
        this._contentContainer.appendChild(this._createSyslogSection());
        this._contentContainer.appendChild(this._createACSSection());
        this._rootElem.appendChild(this._controlButtonsContainer);

        this._clickHandler = this._clickHandler.bind(this);

        this._columns = [
            {
                id: "Name",
                width: 350,
                field: "name",
                resizable: false,
                formatter: formatter.nameFormatter,
            },
            {
                id: "value",
                toolTip: "Takes a configuration snapshot after the event",
                field: "value",
                name: "Snapshot",
                resizable: true,
                cssClass: "centered newrole",
                headerCssClass: "centered",
                formatter: formatter.checkboxFormatter,
                editor: checkBoxEditor,
                asyncPostRender: (cellNode, row, dataContext) => {
                    // console.log(cellNode, row, dataContext);
                    if (!dataContext.title) {
                        if (dataContext.workflow) {
                            cellNode[0].classList.add("disabled");
                        }
                    }
                },
            },
        ];
        this._fitTableSize = this._fitTableSize.bind(this);
        this._rows = this._model._acsEvents;

        this._tableOptions = {
            editable: !(this._model._isSeeded || this._model._isInherited) ? true : false,
            enableAddRow: false,
            enableCellNavigation: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            enableAsyncPostRender: true,
            asyncEditorLoading: false,
            rowHeight: 30,
            headerRowHeight: 30,
            autoHeight: false,
            asyncPostRenderDelay: 0,
            fullWidthRows: true,
        };

        // this._resetScheduleToDefault = this._resetScheduleToDefault.bind(this);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();
        if (this._events) {
            this._unregisterGridListeners();
        }
        this._uiUtils.clearMessage();

        if (this._events && this._events.destroy) {
            this._events.destroy();
        }

        let promArr = [
            this._enableSyslogPort.shutdown(),
            this._port.shutdown(),
            this._waitTimeInput.shutdown(),
            this._filterUsernamesButton.shutdown(),
            this._cancelButton.shutdown(),
            this._confirmButton.shutdown(),
        ];

        return Promise.all(promArr)
            .then(() => {
                this._model = undefined;
                this._enableSyslogPort = undefined;
                this._port = undefined;
                this._waitTimeInput = undefined;
                this._filterUsernamesButton = undefined;
                this._cancelButton = undefined;
                this._confirmButton = undefined;
                this._controlButtonsContainer.remove();
                this._controlButtonsContainer = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._events = undefined;
                this._clickHandler = undefined;
            })
            .catch((err) => {
                console.log(err);
            });
    }

    _registerListeners() {
        this._enableSyslogPort.on(
            "UserValue",
            (this._onEnableSyslogPort = this._onEnableSyslogPort.bind(this))
        );
        window.addEventListener("resize", this._fitTableSize);
        this._confirmButton.on("UserClick", (this._confirmAction = this._confirmAction.bind(this)));
        this._cancelButton.on("UserClick", (this._cancelAction = this._cancelAction.bind(this)));
        this.model.on(
            "SyslogOrganizationChanged",
            (this._onSyslogOrganizationChanged = this._onSyslogOrganizationChanged.bind(this))
        );

        this._enableACS.on(
            "UserValue",
            (this._onEnableACSEvents = this._onEnableACSEvents.bind(this))
        );
        this._waitTimeInput.on(
            "UserValue",
            (this._onWaitTimeChange = this._onWaitTimeChange.bind(this))
        );
        this._filterUsernamesButton.on(
            "UserClick",
            (this._onFilterUsernamesClick = this._showFilterUsernamesVeil.bind(this))
        );
        this._model.on("AcsEventsUpdated", () => {
            this._updateEventsGridData();
            this._setDirty();
            this._updateControlButtonsAvailability();
        });
    }

    _registerGridListeners() {
        this._events.onCellChange.subscribe(this._onEventsCellChangeAction.bind(this));
        document.addEventListener("click", this._clickHandler);
    }

    _unregisterGridListeners() {
        this._events.onCellChange.unsubscribe(this._onEventsCellChangeAction);
        document.removeEventListener("click", this._clickHandler);
    }

    _unregisterListeners() {
        this._enableSyslogPort.removeListener("UserValue", this._onEnableSyslogPort);
        this._confirmButton.removeListener("UserClick", this._confirmAction);
        this._cancelButton.removeListener("UserClick", this._cancelAction);
        this.model.removeListener("SyslogOrganizationChanged", this._onSyslogOrganizationChanged);
        this._waitTimeInput.removeListener("UserValue", this._onWaitTimeChange);
        this._enableACS.removeListener("UserValue", this._onEnableACSEvents);
        this._filterUsernamesButton.removeListener("UserClick", this._onFilterUsernamesClick);
        window.removeEventListener("resize", this._fitTableSize);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model.whenModelIsReady().then(() => {
                if (this._rootElem) {
                    // this._registerListeners();
                    if (!this._model._authorizedActions.includes("syslogNgAccess")) {
                        this._enableSyslogPort.setDisabled(true);
                    }
                    if (!this._model._authorizedActions.includes("autoCapturePrefsWrite")) {
                        this._acsSection.classList.add("no-perms");
                        this._enableACS.setDisabled(true);
                        this._waitTimeInput.setDisabled(true);
                        this._filterUsernamesButton.setDisabled(true);
                    }
                    if (this._model._isRootOrg()) {
                        this._enableACS.setVisible(false);
                    }
                    const workflowCol = {
                        id: "workflow",
                        width: 250,
                        toolTip: "Run a workflow after the event",
                        field: "workflow",
                        name: "Workflow",
                        resizable: true,
                        headerCssClass: "centered",
                        formatter: formatter.workflowFormatter,
                        editor: WorkflowEditor,
                        asyncPostRender: (cellNode, row, dataContext) => {
                            // console.log(cellNode, row, dataContext);
                            if (!dataContext.title) {
                                if (!dataContext.workflow && dataContext.value) {
                                    cellNode[0].classList.add("disabled");
                                }
                            }
                        },
                    };
                    if (this._model._isRPALicenseAvailable) {
                        workflowCol.cssClass = "centered newrole";
                    } else {
                        workflowCol.cssClass = "centered newrole disabled";
                    }
                    this._columns.push(workflowCol);
                    this._rows = new Slick.Data.DataView();
                    this._rows.setItems(this._model._acsEvents);

                    this._rows.getItemMetadata = ((original_meta) => (row) => {
                        const item = this._rows.getItem(row);
                        let origMeta = original_meta(item);
                        origMeta = origMeta || {};
                        if (item.title) {
                            origMeta.cssClasses = (origMeta.cssClasses || "") + " " + "title-row";
                        } else {
                            origMeta.cssClasses = (origMeta.cssClasses || "") + " " + item.eventKey;
                        }
                        return origMeta;
                    })(this._rows.getItemMetadata);
                    this.setSlickHeight();
                    this._renderEventsGrid();
                    this._registerListeners();

                    this._fitTableSize();

                    return this._setFormValues(this._model._syslog);
                }
            });
        }
    }

    _fitTableSize() {
        if (this._events) {
            this._events.resizeCanvas();

            this._events.autosizeColumns();
        }
    }

    _renderEventsGrid() {
        this._events = new Slick.Grid(
            this._eventsList,
            this._rows,
            this._columns,
            this._tableOptions
        );

        this._registerGridListeners();
    }

    setSlickHeight() {
        let heightVal = 0;
        if (this._rows && this._rows.getItems()) {
            // 30px each row height, 27px top margin
            heightVal = 30 * this._rows.getItems().length + 27;
            this._eventsList.setAttribute("style", `height: ${heightVal}px;`);
        }
    }

    _updateEventsGridData() {
        this._rows.setItems(this._model._acsEvents);
        this._events.invalidate();
        this._events.render();
    }

    _setFormValues() {
        const sysLogData = this._model._syslog;
        this._enableSyslogPort.setValue(sysLogData.enabled || false);
        this._port.setValue(sysLogData.port || "No Port defined");
        const debounceDelay = this._model._acsConf.debounceDelay;
        this._waitTimeInput.setValue(debounceDelay ? debounceDelay / 60000 : 0); // convert to mins
        this._waitTimeInput.setDisabled(!sysLogData.enabled);
        this._filterUsernamesButton.setDisabled(!sysLogData.enabled);
        this._enableACS.setValue(this._model._acsConf.enabled);
        if (this._model._acsConf.enabled) {
            this._eventsList.classList.remove("disabled");
        } else {
            this._eventsList.classList.add("disabled");
        }
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/
    _updateControlButtonsAvailability() {
        this._toggleControlButtons(!this._model._isDirty);
    }

    _toggleControlButtons(isDisabled) {
        this._cancelButton.disabled = isDisabled;
        this._confirmButton.disabled = isDisabled;
    }

    _createSyslogSection() {
        const syslogSection = document.createDocumentFragment();
        syslogSection.appendChild(this._enableSyslogPort.renderer.getRootElement());

        const portLabel = document.createElement("label");
        portLabel.innerText = "Port";
        portLabel.setAttribute(
            "title",
            "Gluware–assigned port that must used by devices to send syslog messages via UDP to Gluware"
        );
        syslogSection.appendChild(portLabel);
        syslogSection.appendChild(this._port.renderer.getRootElement());

        const waitTimeLabel = document.createElement("label");
        waitTimeLabel.innerText = "Ignore duplicate messages within (minutes)";
        waitTimeLabel.setAttribute(
            "title",
            "Minutes to ignore syslog messages before snapshot taken"
        );
        syslogSection.appendChild(waitTimeLabel);
        syslogSection.appendChild(this._waitTimeInput.renderer.getRootElement());

        const filterUsernamesButton = this._filterUsernamesButton.renderer.getRootElement();
        filterUsernamesButton.classList.add("filter-usernames-button");
        syslogSection.appendChild(filterUsernamesButton);

        return this._createSection(syslogSection, {title: "Syslog Inbound", bordered: true});
    }

    _createACSSection() {
        const acsSection = document.createElement("div");
        acsSection.classList.add("acs-section");
        acsSection.appendChild(this._enableACS.renderer.getRootElement());
        this._eventsList = document.createElement("div");
        this._eventsList.classList.add("events-list");
        acsSection.appendChild(this._eventsList);
        this._acsSection = acsSection;
        return this._createSection(acsSection, {title: "Event Triggers", bordered: true});
    }
    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onEnableSyslogPort(value) {
        _.set(this._model._updatedSyslog, "enabled", value);
        this._waitTimeInput.setDisabled(!value);
        this._filterUsernamesButton.setDisabled(!value);
        if (!value) {
            const debounceDelay = this._model._acsConf.debounceDelay;
            this._waitTimeInput.setValue(debounceDelay ? debounceDelay / 60000 : 0);
            this._model._acsPrefsEntity.setDebounceDelay(debounceDelay);
        }
        this._setDirty();
        this._updateControlButtonsAvailability();
    }

    _onEnableACSEvents(value) {
        Promise.resolve()
            .then(() => {
                if (!value && !this._model._isRootOrg(this._model._acsPrefsEntity.tenantId)) {
                    return this._uiUtils.showDeleteConfirmDialog({
                        message: `The Event Triggers will be updated to use the Triggers defined by the parent organization and cannot be modified.
                    Do you wish to continue?`,
                        confirmButtonName: "Confirm",
                        cancelButtonName: "Cancel",
                    });
                }
                return Promise.resolve();
            })
            .then(() => {
                this._model._acsPrefsEntity.setEnabled(value);
                return this._model.saveAcs();
            })
            .then(() => {
                if (value) {
                    this._eventsList.classList.remove("disabled");
                    // this._model._updatedAcsEventConf = Object.assign({}, this._model._acsEventConf);
                } else {
                    this._eventsList.classList.add("disabled");
                }
                this._updateEventsGridData();
                this._setDirty();
                this._updateControlButtonsAvailability();
            })
            .catch((err) => {
                this._enableACS.setValue(true, false);
                if (err) {
                    this._model._logger.warn(err);
                } else {
                    this._model._logger.info("EventsRenderer::Disable ACS Events::Err");
                }
            });
    }

    _onWaitTimeChange(value) {
        this._model._acsPrefsEntity.setDebounceDelay(value * 60000); //convert to milliseconds
        this._model._acsConf.debounceDelay = value * 60000;
        this._setDirty();
        this._updateControlButtonsAvailability();
    }

    _showFilterUsernamesVeil() {
        const component = new FilterUsernamesComponent(this._model._updatedSyslog);
        this._model._userFeedback
            .displayCustom(component, {notDismissable: false})
            .then(() => {
                this._setDirty();
                this._updateControlButtonsAvailability();
            })
            .catch((error) => {
                if (error && error.userRejected === true) {
                    return;
                }
                return error && console.error(error);
            });
    }

    _onEventsCellChangeAction(e, {item, row, grid}) {
        // console.log(item, column, row, cell, grid);
        let val = {};
        if (item.value) {
            val.enabled = true;
            val.workflowId = null;
            item.workflow = null;
        } else {
            val.enabled = false;
        }

        if (item.workflow) {
            val.workflowId = item.workflow;
            val.enabled = true;
        }
        if (!item.workflow && !item.value) {
            _.unset(this._model._updatedAcsEventConf, item.eventKey);
        } else {
            _.set(this._model._updatedAcsEventConf, item.eventKey, val);
        }

        grid.resizeCanvas();
        if (this._model._isRPALicenseAvailable) {
            this.toggleFields(val, row, grid);
        }

        this._setDirty();
        this._updateControlButtonsAvailability();
    }

    toggleFields(val, row, grid) {
        //assuming snapshot as column 2 and workflow as column 3
        // enabled true and workflow id truthy --> snapshot disabled, workflow enabled
        // enabled true and workflow id falsy --> snapshot enabled, workflow disabled
        // enabled false --> snapshot and workflow enabled
        const workflowCell = grid.getCellNode(row, 2)[0];
        const snapshotCell = grid.getCellNode(row, 1)[0];
        if (!workflowCell || !snapshotCell) {
            console.warn("Err::Unable to find cells");
            return;
        }
        if (val.enabled && val.workflowId) {
            snapshotCell.classList.add("disabled");
            workflowCell.classList.remove("disabled");
            return;
        }
        if (val.enabled && !val.workflowId) {
            snapshotCell.classList.remove("disabled");
            workflowCell.classList.add("disabled");
            return;
        }
        if (!val.enabled) {
            snapshotCell.classList.remove("disabled");
            workflowCell.classList.remove("disabled");
            return;
        }
    }

    _confirmAction() {
        this._model
            .save()
            .then(() => {
                this._setFormValues();
                this._model._setAcsEventConfVars();
                this._updateEventsGridData();
                this._updateControlButtonsAvailability();
            })
            .catch((err) => {
                this._model._logger.warn("Save error ::", err);
                this._uiUtils.writeErrorMessage(
                    "Save Error :: " + err.errorCode + " :: " + err.errorMsg
                );
            });
    }

    _cancelAction() {
        this._model.cancel().then(() => {
            this._setFormValues();
            this._updateEventsGridData();
            this._updateControlButtonsAvailability();
        });
    }

    _clickHandler(e) {
        this._saveAndCloseEditCellAction(e);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    _onSyslogOrganizationChanged(orgObj) {
        this.model
            .resetConfigData(orgObj)
            .then(() => {
                if (this._model._isRootOrg(orgObj.id)) {
                    this._enableACS.setVisible(false);
                } else {
                    this._enableACS.setVisible(true);
                }
                this._setFormValues();
                this._updateEventsGridData();
                this._updateControlButtonsAvailability();
            })
            .catch((err) => {
                this.model._logger.warn(err, "SyslogRender._onSyslogOrganizationChanged() error.");
            });
    }

    _setDirty() {
        this._model._isDirty =
            !_.isEqual(this._model._syslog, this._model._updatedSyslog) ||
            !_.isEqual(this._model._acsEventConf, this._model._updatedAcsEventConf) ||
            this._model._acsPrefsEntity.isDirty();
    }

    _saveAndCloseEditCellAction(e) {
        //do nothing if no active editors
        if (this._events && !this._events.getEditorLock().isActive()) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._permissionSTContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._events.getEditorLock().commitCurrentEdit();
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = EventsRenderer;
